import React, { useState, useCallback, useMemo, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Sheet from '@amzn/meridian/sheet';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Box from '@amzn/meridian/box';
import closeMediumTokens from '@amzn/meridian-tokens/base/icon/close-medium';
import filterTokens from '@amzn/meridian-tokens/base/icon/filter';
import Heading from '@amzn/meridian/heading';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import {
  OutlierType,
  FilterValues,
  UniversalFilterValue,
  FilterKey,
  FilterValueType,
} from '../types/types';
import {
  createFilterChangeHandler,
  getWeeksListForFilter,
} from '../filterHelpers';

import { ContactStatusOptions } from '@amzn/aet-contactrecommendation-kit';
import { getOutlierI18nKey } from '../helpers';
import { StateContext } from '../../../GlobalStateStore';

interface FilterPanelProps {
  categories: OutlierType[];
  associates: string[];
  pastWeek: string;
  filters: FilterValues;
  onFilterChange: (newFilters: Partial<FilterValues>) => void;
  onClearFilters: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  categories,
  associates,
  pastWeek,
  filters,
  onFilterChange,
  onClearFilters,
}) => {
  const [open, setOpen] = useState(false);
  const intl = useIntl();

  const onClickOpen = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);

  const weekOptions = useMemo(() => getWeeksListForFilter(), []);

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;

  const sortedAssociates = [...associates].sort();

  const commonClickstreamPattern = {
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-TM': isTm,
    'data-csa-c-is-OM': isOm,
  };

  const handleStatusClickstream = (status: any) => {
    let commonPatternForStatusClickstream = {
      'data-csa-c-type': 'select',
      'data-csa-c-slot-id':
        'tm-outlier-management-filter-outliers-status-select-slot',
      'data-csa-c-tag1': 'Coaching',
      'data-csa-c-tag2': 'Outlier Metrics',
      'data-csa-c-tag3': 'Filter Outliers',
      ...commonClickstreamPattern,
    };

    switch (status) {
      case UniversalFilterValue.ALL:
        return {
          ...commonPatternForStatusClickstream,
          'data-csa-c-tag4': 'All',
        };
      case ContactStatusOptions.UNDEFINED:
        return {
          ...commonPatternForStatusClickstream,
          'data-csa-c-tag4': 'Pending',
        };
      case ContactStatusOptions.REVIEWED:
        return {
          ...commonPatternForStatusClickstream,
          'data-csa-c-tag4': 'Reviewed',
        };
      case ContactStatusOptions.DRAFT:
        return {
          ...commonPatternForStatusClickstream,
          'data-csa-c-tag4': 'Draft',
        };
      case ContactStatusOptions.DELIVERED:
        return {
          ...commonPatternForStatusClickstream,
          'data-csa-c-tag4': 'Delivered',
        };
    }
  };

  const handleFilterChange = useCallback(
    createFilterChangeHandler(onFilterChange),
    [onFilterChange]
  );

  const preselectedCategoryExists = categories.some(
    (category: FilterValueType[typeof FilterKey.CATEGORY]) =>
      category === filters.category
  );

  const preselectedAssociateExists = sortedAssociates.some(
    (associate: FilterValueType[typeof FilterKey.ASSOCIATE]) =>
      associate === filters.associate
  );

  return (
    <>
      <Button type='icon' onClick={onClickOpen}>
        <Icon tokens={filterTokens}>
          <FormattedMessage
            id='cs_perfportal_filter_button'
            defaultMessage='Filter'
          />
        </Icon>
      </Button>
      <Sheet type='overlay' open={open} onClose={onClose}>
        <Row alignmentVertical='center' alignmentHorizontal='justify'>
          <Heading level={3}>
            <FormattedMessage
              id='cs_perfportal_filter_outliers_title'
              defaultMessage='Filter Outliers'
            />
          </Heading>
          <Button onClick={onClose} type='icon'>
            <Icon tokens={closeMediumTokens}>
              <FormattedMessage
                id='cs_perfportal_close_sheet'
                defaultMessage='Close sheet'
              />
            </Icon>
          </Button>
        </Row>
        <Column spacing='medium' spacingInset='400 none none none'>
          <Box spacingInset='200 none'>
            <Column spacing='small'>
              <Text color='secondary'>
                <FormattedMessage
                  id='cs_perfportal_filter_week_of_label'
                  defaultMessage='Week of'
                />
              </Text>
              <Select
                value={filters.timeOption}
                onChange={(
                  value: FilterValueType[typeof FilterKey.TIME_OPTION]
                ) => handleFilterChange(FilterKey.TIME_OPTION, value)}
                width={240}
              >
                {weekOptions.map(week => (
                  <SelectOption key={week} label={week} value={week} />
                ))}
              </Select>
            </Column>
          </Box>
          <Box spacingInset='200 none'>
            <Column spacing='small'>
              <Text color='secondary'>
                <FormattedMessage
                  id='cs_perfportal_outlier_filter_category_label'
                  defaultMessage='Outlier Category'
                />
              </Text>
              <Select
                value={
                  preselectedCategoryExists
                    ? filters.category
                    : UniversalFilterValue.ALL
                }
                onChange={(value: FilterValueType[typeof FilterKey.CATEGORY]) =>
                  handleFilterChange(FilterKey.CATEGORY, value)
                }
                width={240}
              >
                <SelectOption
                  label={intl.formatMessage({
                    id: 'cs_perfportal_filter_all',
                    defaultMessage: 'All',
                  })}
                  value={UniversalFilterValue.ALL}
                  data-csa-c-type='select'
                  data-csa-c-slot-id='tm-outlier-management-filter-outliers-category-select-slot'
                  data-csa-c-tag1='Coaching'
                  data-csa-c-tag2='Outlier Metrics'
                  data-csa-c-tag3='Filter Outliers'
                  data-csa-c-tag4={'All'}
                  {...commonClickstreamPattern}
                />
                {categories.map(category => (
                  <SelectOption
                    key={category}
                    label={intl.formatMessage({
                      id: getOutlierI18nKey(category),
                      defaultMessage: category,
                    })}
                    value={category}
                    data-csa-c-type='select'
                    data-csa-c-slot-id='tm-outlier-management-filter-outliers-category-select-slot'
                    data-csa-c-tag1='Coaching'
                    data-csa-c-tag2='Outlier Metrics'
                    data-csa-c-tag3='Filter Outliers'
                    data-csa-c-tag4={category}
                    {...commonClickstreamPattern}
                  />
                ))}
              </Select>
            </Column>
          </Box>
          <Box spacingInset='200 none'>
            <Column spacing='small'>
              <Text color='secondary'>
                <FormattedMessage
                  id='cs_perfportal_associate'
                  defaultMessage='Associate'
                />
              </Text>
              <Select
                value={
                  preselectedAssociateExists
                    ? filters.associate
                    : UniversalFilterValue.ALL
                }
                onChange={(
                  value: FilterValueType[typeof FilterKey.ASSOCIATE]
                ) => handleFilterChange(FilterKey.ASSOCIATE, value)}
                width={240}
              >
                <SelectOption
                  label={intl.formatMessage({
                    id: 'cs_perfportal_filter_all',
                    defaultMessage: 'All',
                  })}
                  value={UniversalFilterValue.ALL}
                  data-csa-c-type='select'
                  data-csa-c-slot-id='tm-outlier-management-filter-outliers-associate-select-slot'
                  data-csa-c-tag1='Coaching'
                  data-csa-c-tag2='Outlier Metrics'
                  data-csa-c-tag3='Filter Outliers'
                  data-csa-c-tag4={'All'}
                  {...commonClickstreamPattern}
                />
                {sortedAssociates.map(associate => (
                  <SelectOption
                    key={associate}
                    label={associate}
                    value={associate}
                    data-csa-c-type='select'
                    data-csa-c-slot-id='tm-outlier-management-filter-outliers-associate-select-slot'
                    data-csa-c-tag1='Coaching'
                    data-csa-c-tag2='Outlier Metrics'
                    data-csa-c-tag3='Filter Outliers'
                    data-csa-c-tag4={associate}
                    {...commonClickstreamPattern}
                  />
                ))}
              </Select>
            </Column>
          </Box>
          <Box spacingInset='200 none'>
            <Column spacing='small'>
              <Text color='secondary'>
                <FormattedMessage
                  id='cs_perfportal_filter_status_label'
                  defaultMessage='Status'
                />
              </Text>
              <Select
                value={filters.status}
                onChange={(value: FilterValueType[typeof FilterKey.STATUS]) =>
                  handleFilterChange(FilterKey.STATUS, value)
                }
                width={240}
              >
                <SelectOption
                  label={intl.formatMessage({
                    id: 'cs_perfportal_filter_all',
                    defaultMessage: 'All',
                  })}
                  value={UniversalFilterValue.ALL}
                  {...handleStatusClickstream(UniversalFilterValue.ALL)}
                />
                <SelectOption
                  label={intl.formatMessage({
                    id: 'cs_perfportal_filter_status_pending',
                    defaultMessage: 'Pending',
                  })}
                  value={ContactStatusOptions.UNDEFINED}
                  {...handleStatusClickstream(ContactStatusOptions.UNDEFINED)}
                />
                <SelectOption
                  label={intl.formatMessage({
                    id: 'cs_perfportal_filter_status_reviewed',
                    defaultMessage: 'Reviewed',
                  })}
                  value={ContactStatusOptions.REVIEWED}
                  {...handleStatusClickstream(ContactStatusOptions.REVIEWED)}
                />
                <SelectOption
                  label={intl.formatMessage({
                    id: 'cs_perfportal_filter_status_delivered',
                    defaultMessage: 'Delivered',
                  })}
                  value={ContactStatusOptions.DELIVERED}
                  {...handleStatusClickstream(ContactStatusOptions.DELIVERED)}
                />
                <SelectOption
                  label={intl.formatMessage({
                    id: 'cs_perfportal_filter_status_draft',
                    defaultMessage: 'Draft',
                  })}
                  value={ContactStatusOptions.DRAFT}
                  {...handleStatusClickstream(ContactStatusOptions.DRAFT)}
                />
              </Select>
            </Column>
          </Box>
          <Row
            width='100%'
            widths='fill'
            alignmentHorizontal='center'
            spacing='medium'
            spacingInset='200 none'
          >
            <Button
              size='medium'
              onClick={() => {
                onClearFilters();
                onClose();
              }}
              type='secondary'
              data-csa-c-type='button'
              data-csa-c-slot-id='tm-outlier-management-filter-outliers-reset-button-slot'
              data-csa-c-tag1='Coaching'
              data-csa-c-tag2='Outlier Metrics'
              data-csa-c-tag3='Filter Outliers'
              {...commonClickstreamPattern}
            >
              <FormattedMessage
                id='cs_perfportal_filter_reset'
                defaultMessage='Reset filters'
              />
            </Button>
          </Row>
        </Column>
      </Sheet>
    </>
  );
};
